/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useState } from "react";
import {
  Megaphone,
  Calendar,
  Target,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Plus,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart, Area,
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { toast } from "sonner";
import { authFetch, API } from "../utils/api";

const BRAND = "#87CEEB";
const BRAND_DARK = "#4FA3C7";
const PIE_COLORS = ["#87CEEB", "#7B61FF", "#F59E0B", "#10B981", "#EF4444", "#0EA5E9", "#EC4899", "#6366F1", "#84CC16", "#F97316", "#14B8A6", "#A855F7"];

const fmtAUD = (n) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(Number(n || 0));
const fmtAUD2 = (n) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(Number(n || 0));

const labelForBucket = (b, period) => {
  if (!b) return "";
  if (period === "day") {
    const [, m, d] = b.split("-");
    return `${d}/${m}`;
  }
  if (period === "week") return b.replace("-W", " W");
  // month
  const [y, m] = b.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("en-AU", { month: "short", year: "2-digit" });
};

export default function FinancePlanning({ categories = [], onChange }) {
  const [period, setPeriod] = useState("day");
  const [days, setDays] = useState(30);
  const [category, setCategory] = useState("");
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const month = useMemo(() => new Date().toISOString().slice(0, 7), []);

  const reload = () => setReloadKey((k) => k + 1);
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const q = new URLSearchParams({ period, days: String(days) });
        if (category) q.set("category", category);
        const [b, p] = await Promise.all([
          authFetch(`${API}/admin/finance/expenses/breakdown?${q.toString()}`).then((r) => r.json()),
          authFetch(`${API}/admin/finance/budgets/progress?month=${month}`).then((r) => r.json()),
        ]);
        if (!alive) return;
        setData(b);
        setProgress(p);
      } catch (err) {
        console.error("[planning] load failed", err);
        toast.error("Could not load planning data");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [period, days, category, month, reloadKey]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500">
        <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading planning view…
      </div>
    );
  }

  const series = data.series.map((s) => ({ ...s, label: labelForBucket(s.bucket, period) }));
  const totalRange = data.total;
  const periodLabel = period === "day" ? "day" : period === "week" ? "week" : "month";

  return (
    <div className="space-y-6" data-testid="finance-planning">
      {/* ─── Controls ─── */}
      <div className="rounded-2xl bg-white border border-slate-100 p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
          {[
            { k: "day", label: "Daily" },
            { k: "week", label: "Weekly" },
            { k: "month", label: "Monthly" },
          ].map((p) => (
            <button
              key={p.k}
              onClick={() => setPeriod(p.k)}
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                period === p.k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
              data-testid={`planning-period-${p.k}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white"
          data-testid="planning-days-select"
        >
          {[7, 14, 30, 60, 90, 180, 365].map((d) => (
            <option key={d} value={d}>Last {d} days</option>
          ))}
        </select>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white min-w-[200px]"
          data-testid="planning-category-filter"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <div className="ml-auto text-xs text-slate-500">
          <strong className="text-slate-900">{fmtAUD2(totalRange)}</strong> spent in the last {data.days} days
        </div>
      </div>

      {/* ─── KPI strip ─── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={Megaphone} label={category || "All expenses"} value={fmtAUD(totalRange)} hint={`Total · last ${data.days} days`} color={BRAND_DARK} />
        <KpiCard icon={Calendar} label="Daily avg" value={fmtAUD(data.daily_average)} hint="Across the selected range" color="#10B981" />
        <KpiCard icon={TrendingUp} label="Weekly avg" value={fmtAUD(data.weekly_average)} hint="7 × daily average" color="#7B61FF" />
        <KpiCard icon={Target} label="Monthly avg" value={fmtAUD(data.monthly_average)} hint="30 × daily average" color="#F59E0B" />
      </div>

      {/* ─── Main chart + Donut ─── */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl bg-white border border-slate-100 p-5">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-base font-bold text-slate-900">Spend per {periodLabel}</h3>
            <span className="text-xs text-slate-400">{series.length} {periodLabel}s</span>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            {category ? `Just '${category}'` : "All categories combined"} — average <strong className="text-slate-900">{fmtAUD2(data.average_per_bucket)}</strong>/{periodLabel}
          </p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="planArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={BRAND} stopOpacity={0.7} />
                    <stop offset="100%" stopColor={BRAND} stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" stroke="#94a3b8" fontSize={11} interval="preserveStartEnd" />
                <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={(v) => `$${v}`} width={50} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                  formatter={(v) => [fmtAUD2(v), "Spend"]}
                />
                <Area type="monotone" dataKey="amount" stroke={BRAND_DARK} strokeWidth={2.5} fill="url(#planArea)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-slate-100 p-5">
          <h3 className="text-base font-bold text-slate-900">By category</h3>
          <p className="text-xs text-slate-500 mb-3">Where the money's going</p>
          {data.by_category.length === 0 ? (
            <div className="text-sm text-slate-400 py-10 text-center">
              No expenses recorded yet.
            </div>
          ) : (
            <>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.by_category}
                      dataKey="amount"
                      nameKey="category"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={75}
                      paddingAngle={2}
                    >
                      {data.by_category.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                      formatter={(v, n) => [fmtAUD2(v), n]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3 space-y-1.5 max-h-44 overflow-y-auto">
                {data.by_category.map((c, i) => {
                  const pct = totalRange ? Math.round((c.amount / totalRange) * 100) : 0;
                  return (
                    <button
                      key={c.category}
                      onClick={() => setCategory(category === c.category ? "" : c.category)}
                      className="w-full flex items-center justify-between gap-2 text-xs py-1.5 px-2 rounded-lg hover:bg-slate-50 transition-colors"
                      title="Click to filter this category"
                    >
                      <span className="flex items-center gap-2 truncate">
                        <span className="h-2.5 w-2.5 rounded-full flex-none" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                        <span className="truncate text-slate-700">{c.category}</span>
                      </span>
                      <span className="flex items-center gap-2 flex-none">
                        <span className="tabular-nums text-slate-500">{pct}%</span>
                        <span className="font-bold text-slate-900 tabular-nums">{fmtAUD(c.amount)}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ─── Daily heatmap (when period=day) ─── */}
      {period === "day" && (
        <DailyHeatmap series={series} />
      )}

      {/* ─── Budget tracking ─── */}
      <BudgetPanel
        progress={progress}
        categories={categories}
        month={month}
        onSaved={() => { reload(); onChange && onChange(); }}
      />
    </div>
  );
}

/* ─── Helpers ─── */
const KpiCard = ({ icon: Icon, label, value, hint, color = BRAND }) => (
  <div className="rounded-2xl bg-white border border-slate-100 p-5 shadow-sm">
    <div className="flex items-center justify-between mb-3">
      <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: `${color}22`, color }}>
        <Icon className="w-5 h-5" />
      </div>
      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold truncate max-w-[55%] text-right">{label}</span>
    </div>
    <div className="text-2xl sm:text-3xl font-extrabold text-slate-900 leading-tight">{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
  </div>
);

function DailyHeatmap({ series }) {
  // Pick the max for colour intensity scaling
  const max = Math.max(1, ...series.map((s) => s.amount));
  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-5">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-base font-bold text-slate-900">Spend heatmap</h3>
        <span className="text-xs text-slate-400">Darker = bigger day</span>
      </div>
      <p className="text-xs text-slate-500 mb-4">Quick-glance daily spend pattern — useful for spotting marketing bursts.</p>
      <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
        {series.slice(-49).map((s) => {
          const intensity = s.amount / max;
          const bg = s.amount === 0
            ? "#f1f5f9"
            : `rgba(75, 163, 199, ${Math.max(0.18, intensity).toFixed(2)})`;
          return (
            <div
              key={s.bucket}
              title={`${s.bucket}\n${fmtAUD2(s.amount)}`}
              className="aspect-square rounded-md flex items-end justify-end p-1 text-[9px] font-semibold text-slate-700"
              style={{ background: bg }}
            >
              {new Date(s.bucket).getDate()}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BudgetPanel({ progress, categories, month, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: categories[0] || "", amount: "" });
  const [saving, setSaving] = useState(false);

  if (!progress) return null;

  const save = async () => {
    if (!form.category || !form.amount) return toast.error("Category and amount required");
    setSaving(true);
    try {
      const r = await authFetch(`${API}/admin/finance/budgets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ month, category: form.category, amount: Number(form.amount) }),
      });
      if (!r.ok) throw new Error("save failed");
      toast.success(`Budget for ${form.category} set`);
      setOpen(false);
      setForm({ ...form, amount: "" });
      onSaved();
    } catch (err) {
      console.error(err);
      toast.error("Could not save budget");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (cat) => {
    if (!window.confirm(`Remove budget for ${cat}?`)) return;
    const q = new URLSearchParams({ month, category: cat });
    const r = await authFetch(`${API}/admin/finance/budgets?${q.toString()}`, { method: "DELETE" });
    if (r.ok) { toast.success("Budget removed"); onSaved(); }
  };

  const pacingTone = (status) => {
    if (status === "over") return { bg: "bg-rose-50", bar: "bg-rose-500", chip: "bg-rose-100 text-rose-700", icon: AlertTriangle };
    if (status === "warning") return { bg: "bg-amber-50", bar: "bg-amber-500", chip: "bg-amber-100 text-amber-700", icon: AlertTriangle };
    if (status === "no_budget") return { bg: "bg-slate-50", bar: "bg-slate-400", chip: "bg-slate-200 text-slate-700", icon: AlertTriangle };
    if (status === "ahead") return { bg: "bg-emerald-50", bar: "bg-emerald-500", chip: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 };
    return { bg: "bg-sky-50", bar: "bg-[#87CEEB]", chip: "bg-[#87CEEB]/15 text-[#4FA3C7]", icon: CheckCircle2 };
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-5" data-testid="planning-budgets-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-bold text-slate-900">Budgets — {month}</h3>
          <p className="text-xs text-slate-500">
            Day {progress.days_elapsed} of {progress.days_in_month} ·
            Total budget {fmtAUD2(progress.totals.budget)} ·
            Spent {fmtAUD2(progress.totals.spent)}
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#87CEEB] hover:bg-[#6BB8DB] text-white" data-testid="planning-add-budget-btn">
              <Plus className="w-4 h-4 mr-1" /> Set / update budget
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Set budget for {month}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Category</Label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm bg-white"
                  data-testid="planning-budget-cat"
                >
                  {categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Monthly budget (AUD)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="e.g. 3000"
                  data-testid="planning-budget-amount"
                />
                {form.amount && (
                  <p className="text-xs text-slate-500 mt-1">
                    That's <strong>{fmtAUD2(Number(form.amount) / 30)}/day</strong> on average
                  </p>
                )}
              </div>
              <Button onClick={save} disabled={saving} className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="planning-budget-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save budget"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {progress.rows.length === 0 ? (
        <div className="text-sm text-slate-400 py-10 text-center">
          No budgets yet — set one above to start tracking against your spend.
        </div>
      ) : (
        <div className="space-y-3">
          {progress.rows.map((r) => {
            const tone = pacingTone(r.status);
            const ToneIcon = tone.icon;
            const pct = Math.min(100, Math.max(0, r.pct_used));
            return (
              <div key={r.category} className={`rounded-xl p-4 ${tone.bg}`} data-testid={`budget-row-${r.category.replace(/[^a-z]/gi, '-').toLowerCase()}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-900 truncate">{r.category}</span>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold ${tone.chip}`}>
                        <ToneIcon className="w-2.5 h-2.5 inline mr-1" />
                        {r.status.replace("_", " ")}
                      </span>
                    </div>
                    {r.budget > 0 && (
                      <p className="text-xs text-slate-500 mt-0.5">
                        Daily budget <strong className="text-slate-900">{fmtAUD2(r.daily_budget)}</strong> ·
                        expected by today <strong className="text-slate-900">{fmtAUD2(r.expected_spend_so_far)}</strong>
                      </p>
                    )}
                    {r.status === "no_budget" && (
                      <p className="text-xs text-slate-500 mt-0.5">
                        Spending detected with no budget set — consider adding one.
                      </p>
                    )}
                  </div>
                  <div className="text-right flex-none">
                    <div className="text-base font-extrabold text-slate-900 tabular-nums">{fmtAUD2(r.spent)}</div>
                    <div className="text-[11px] text-slate-500">of {fmtAUD2(r.budget)}</div>
                  </div>
                </div>
                {r.budget > 0 && (
                  <>
                    <div className="h-2 bg-white rounded-full overflow-hidden">
                      <div className={`h-full ${tone.bar} transition-all`} style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex items-center justify-between mt-1.5 text-[11px]">
                      <span className="font-semibold text-slate-700">{r.pct_used}% used</span>
                      <span className="text-slate-500">
                        {r.remaining >= 0 ? `${fmtAUD2(r.remaining)} left` : `${fmtAUD2(-r.remaining)} over`}
                      </span>
                    </div>
                  </>
                )}
                {r.budget > 0 && (
                  <button
                    onClick={() => remove(r.category)}
                    className="text-[10px] uppercase tracking-wider text-slate-400 hover:text-rose-600 mt-2"
                  >
                    Remove budget
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
